import { useCallback, useMemo } from 'react'

import { useApolloClient } from '@apollo/client'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import {
  AddEntityVersionsToRecordMutation,
  AddEntityVersionsToRecordMutationVariables,
  GetDetailedEntityVersionByIdQuery,
  GetDetailedEntityVersionByIdQueryVariables,
  GetEntityVersionsByIdQuery,
  GetEntityVersionsByIdQueryVariables,
  GetSearchEntityVersionByIdQuery,
  GetSearchEntityVersionByIdQueryVariables,
  RemoveEntityVersionFromRecordMutation,
  RemoveEntityVersionFromRecordMutationVariables,
  SearchEntityVersionsByCoordinateInput,
  SearchEntityVersionsByCoordinateQuery,
  SearchEntityVersionsByCoordinateQueryVariables,
  SearchEntityVersionsInput,
  SearchEntityVersionsQuery,
  SearchEntityVersionsQueryVariables,
  SearchEntityVersionsByBoundingBoxQuery,
  SearchEntityVersionsByBoundingBoxQueryVariables,
  SearchEntityVersionsByLocationInput,
  SearchEntityVersionsByLocationQuery,
  SearchEntityVersionsByLocationQueryVariables,
  GetEntityVersionByLocationCountsInput,
  GetEntityVersionByLocationCountsQuery,
  GetEntityVersionByLocationCountsQueryVariables,
} from 'types/graphql'

import { MapBoundaries } from 'src/components/shared/ArcGisMap/types'

import { QueryKey, useMutation, useQuery } from '../utils/queryClient'

const GetMultiple = gql`
  query GetEntityVersionsByIdQuery($ids: [Int!]!) {
    entityVersionsById(ids: $ids) {
      ...EntityVersionFragment
    }
  }
  fragment EntityVersionFragment on EntityVersion {
    id
    entity {
      id
      name
    }
  }
`

export const useGetEntityVersionsById = (ids: number[]) => {
  const { data, ...rest } = useQuery<
    GetEntityVersionsByIdQuery,
    GetEntityVersionsByIdQueryVariables
  >({
    queryKey: [QueryKey.GetEntityVersionsById, ids],
    queryDocument: GetMultiple,
    variables: { ids },
    enabled: !!ids.length,
  })
  const entityVersions = useMemo(() => {
    return data?.entityVersionsById || []
  }, [data])

  return {
    ...rest,
    entityVersions,
  }
}

const AddEntityVersionsToRecord = gql`
  mutation AddEntityVersionsToRecordMutation(
    $recordId: Int!
    $entityVersionIds: [Int!]!
    $manuallyEnteredStrings: [String!]!
  ) {
    addEntityVersionsToRecord(
      recordId: $recordId
      entityVersionIds: $entityVersionIds
      manuallyEnteredStrings: $manuallyEnteredStrings
    ) {
      id
    }
  }
`

export const useAddEntityVersionsToRecordMutation = () => {
  return useMutation<AddEntityVersionsToRecordMutation, AddEntityVersionsToRecordMutationVariables>(
    {
      mutationDocument: AddEntityVersionsToRecord,
    }
  )
}

const RemoveEntityVersionFromRecord = gql`
  mutation RemoveEntityVersionFromRecordMutation(
    $recordId: Int!
    $entityVersionId: Int
    $manuallyEnteredString: String
  ) {
    removeEntityVersionFromRecord(
      recordId: $recordId
      entityVersionId: $entityVersionId
      manuallyEnteredString: $manuallyEnteredString
    ) {
      id
    }
  }
`

export const useRemoveEntityVersionFromRecordMutation = () => {
  return useMutation<
    RemoveEntityVersionFromRecordMutation,
    RemoveEntityVersionFromRecordMutationVariables
  >({ mutationDocument: RemoveEntityVersionFromRecord })
}

const GetDetailedEntityVersionByIds = gql`
  query GetDetailedEntityVersionByIdQuery($ids: [Int!]!) {
    entityVersionsById(ids: $ids) {
      ...EntityVersionWithFieldsFragment
    }
  }
`

export const useGetEntityVersionByIds = (ids: number[] | null | undefined) => {
  const { data, ...rest } = useQuery<
    GetDetailedEntityVersionByIdQuery,
    GetDetailedEntityVersionByIdQueryVariables
  >({
    queryKey: [QueryKey.GetEntityVersionById, ids],
    queryDocument: GetDetailedEntityVersionByIds,
    variables: { ids: ids ?? [] },
    enabled: !!ids?.length,
    placeholderData: keepPreviousData,
  })
  const entityVersions = useMemo(() => {
    return data?.entityVersionsById || []
  }, [data])
  return {
    ...rest,
    entityVersions,
  }
}

const SearchEntityVersions = gql`
  query SearchEntityVersionsQuery($input: SearchEntityVersionsInput!) {
    searchEntityVersions(input: $input) {
      entityVersions {
        ...SearchEntityVersionFragment
      }
    }
  }
`

export const useSearchEntityVersionsQuery = (input: SearchEntityVersionsInput) => {
  const { data, ...rest } = useQuery<SearchEntityVersionsQuery, SearchEntityVersionsQueryVariables>(
    {
      queryKey: [QueryKey.SearchEntityVersions, input],
      queryDocument: SearchEntityVersions,
      variables: { input },
      enabled: !!input.entityLayerIds?.length && !!input.query,
      placeholderData: keepPreviousData,
    }
  )
  const entityVersions = useMemo(() => {
    return data?.searchEntityVersions?.entityVersions || []
  }, [data])
  return {
    entityVersions,
    ...rest,
  }
}

const GetSearchEntityVersionById = gql`
  query GetSearchEntityVersionByIdQuery($id: Int!) {
    entityVersionsById(ids: [$id]) {
      ...SearchEntityVersionFragment
    }
  }
`

export const useGetSearchEntityVersionByIdQuery = (id?: number | null) => {
  const { data, ...rest } = useQuery<
    GetSearchEntityVersionByIdQuery,
    GetSearchEntityVersionByIdQueryVariables
  >({
    queryKey: [QueryKey.GetSearchEntityVersionById, id],
    queryDocument: GetSearchEntityVersionById,
    variables: { id: id ?? 0 },
    enabled: !!id,
  })

  const entityVersion = useMemo(() => {
    return data?.entityVersionsById?.[0] || null
  }, [data])

  return {
    entityVersion,
    ...rest,
  }
}

const SearchEntityVersionsByCoordinate = gql`
  query SearchEntityVersionsByCoordinateQuery($input: SearchEntityVersionsByCoordinateInput!) {
    searchEntityVersionsByCoordinate(input: $input) {
      ...SearchEntityVersionsByCoordinateFragment
    }
  }
`

export const useSearchEntityVersionsByCoordinateQuery = () => {
  const queryClient = useQueryClient()
  const client = useApolloClient()
  return useCallback(
    (input: SearchEntityVersionsByCoordinateInput) =>
      queryClient.fetchQuery<
        SearchEntityVersionsByCoordinateQuery,
        SearchEntityVersionsByCoordinateQueryVariables
      >({
        queryKey: [QueryKey.SearchEntityVersionsByCoordinate, input],
        queryFn: ({ signal }) =>
          client
            .query<
              SearchEntityVersionsByCoordinateQuery,
              SearchEntityVersionsByCoordinateQueryVariables
            >({
              query: SearchEntityVersionsByCoordinate,
              variables: { input },
              context: {
                fetchOptions: {
                  signal,
                },
              },
            })
            .then(({ data }) => data),
      }),
    [client, queryClient]
  )
}

const SearchEntityVersionsByBoundingBox = gql`
  query SearchEntityVersionsByBoundingBoxQuery($input: SearchEntityVersionsByBoundingBoxInput!) {
    searchEntityVersionsByBoundingBox(input: $input) {
      ...SearchEntityVersionsByBoundingBoxFragment
    }
  }
`

export const useSearchEntityVersionsByBoundingBoxQuery = (
  entityLayerId: number | null | undefined,
  mapBoundaries: MapBoundaries | null
) => {
  const { data, ...rest } = useQuery<
    SearchEntityVersionsByBoundingBoxQuery,
    SearchEntityVersionsByBoundingBoxQueryVariables
  >({
    queryKey: [QueryKey.SearchEntityVersionsByBoundingBox, entityLayerId, mapBoundaries],
    queryDocument: SearchEntityVersionsByBoundingBox,
    variables: {
      input: {
        entityLayerId: entityLayerId ?? 0,
        minLatitude: mapBoundaries?.minLatitude ?? 0,
        maxLatitude: mapBoundaries?.maxLatitude ?? 0,
        minLongitude: mapBoundaries?.minLongitude ?? 0,
        maxLongitude: mapBoundaries?.maxLongitude ?? 0,
      },
    },
    enabled: !!entityLayerId && !!mapBoundaries,
    placeholderData: keepPreviousData,
  })

  const entityVersions = useMemo(() => {
    return data?.searchEntityVersionsByBoundingBox ?? []
  }, [data])

  return {
    entityVersions,
    ...rest,
  }
}

const SearchEntityVersionsByLocation = gql`
  query SearchEntityVersionsByLocationQuery($input: SearchEntityVersionsByLocationInput!) {
    searchEntityVersionsByLocation(input: $input) {
      ...SearchEntityVersionsByLocationFragment
    }
  }
`

export const useSearchEntityVersionsByLocationQuery = (
  input: SearchEntityVersionsByLocationInput | null | undefined
) => {
  const { data, ...rest } = useQuery<
    SearchEntityVersionsByLocationQuery,
    SearchEntityVersionsByLocationQueryVariables | void
  >({
    queryKey: [QueryKey.SearchEntityVersionsByLocation, input],
    queryDocument: SearchEntityVersionsByLocation,
    ...(input ? { variables: { input } } : {}),
    enabled: !!input,
    placeholderData: keepPreviousData,
  })

  const entityVersions = useMemo(() => {
    return data?.searchEntityVersionsByLocation ?? []
  }, [data])

  return {
    entityVersions,
    ...rest,
  }
}

const GetEntityVersionByLocationCounts = gql`
  query GetEntityVersionByLocationCountsQuery($input: GetEntityVersionByLocationCountsInput!) {
    getEntityVersionByLocationCounts(input: $input) {
      totalCount
    }
  }
`

export const useGetEntityVersionByLocationCountsQuery = (
  input: GetEntityVersionByLocationCountsInput | null | undefined
) => {
  const { data, ...rest } = useQuery<
    GetEntityVersionByLocationCountsQuery,
    GetEntityVersionByLocationCountsQueryVariables | void
  >({
    queryKey: [QueryKey.GetEntityVersionByLocationCounts, input],
    queryDocument: GetEntityVersionByLocationCounts,
    ...(input ? { variables: { input } } : {}),
    enabled: !!input,
    placeholderData: keepPreviousData,
  })

  const totalCount = data?.getEntityVersionByLocationCounts?.totalCount ?? 0

  return {
    totalCount,
    ...rest,
  }
}
